import React, { useEffect, useMemo, useState } from "react";
import { calculateGearCutting } from "../../utils/calculations";
import ManufacturingDiagram from "../ManufacturingDiagram";

const createGear = (id, teeth, shaftId) => ({
  id,
  teeth,
  shaftId,
});

const createGearId = () =>
  `gear_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const createShaftId = () =>
  `shaft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const formatRatio = (value) => Number(value.toFixed(4));

const gcd = (a, b) => {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));

  while (y !== 0) {
    const temp = y;
    y = x % y;
    x = temp;
  }

  return x || 1;
};

const simplifyRatio = (numerator, denominator) => {
  const safeNumerator = Math.abs(Math.round(Number(numerator)));
  const safeDenominator = Math.abs(Math.round(Number(denominator)));

  if (!safeNumerator || !safeDenominator) {
    return {
      numerator: safeNumerator,
      denominator: safeDenominator,
      text: `${safeNumerator}:${safeDenominator}`,
    };
  }

  const divisor = gcd(safeNumerator, safeDenominator);

  return {
    numerator: safeNumerator / divisor,
    denominator: safeDenominator / divisor,
    text: `${safeNumerator / divisor}:${safeDenominator / divisor}`,
  };
};

const GearRatioCalculator = ({ onSave, savingCalculation }) => {
  const [mode, setMode] = useState("ratio");

  const [gearTrain, setGearTrain] = useState([
    createGear("gear_1", 20, "shaft_1"),
    createGear("gear_2", 40, "shaft_2"),
  ]);

  const [cuttingParams, setCuttingParams] = useState({
    teeth: 24,
    diametralPitch: 16,
  });

  const [cuttingResults, setCuttingResults] = useState(null);

  useEffect(() => {
    setCuttingResults(calculateGearCutting(cuttingParams));
  }, [cuttingParams]);

  const ratioResults = useMemo(() => {
    const errors = {};
    const warnings = [];

    if (!Array.isArray(gearTrain) || gearTrain.length < 2) {
      errors.gearTrain = "At least 2 gears are required.";
      return { errors, confidence: "Invalid" };
    }

    const normalizedGears = gearTrain.map((gear, index) => {
      const teeth = Number(gear.teeth);
      const shaftId = String(gear.shaftId || "").trim();

      if (!teeth || teeth <= 0) {
        errors[`gear_${index}`] = `Gear ${index + 1} tooth count must be greater than zero.`;
      }

      if (!shaftId) {
        errors[`shaft_${index}`] = `Gear ${index + 1} must have a shaft ID.`;
      }

      return {
        ...gear,
        teeth,
        shaftId,
      };
    });

    if (Object.keys(errors).length > 0) {
      return { errors, confidence: "Invalid" };
    }

    const stageRatios = [];
    let overallNumerator = 1;
    let overallDenominator = 1;
    let stageCount = 0;

    for (let i = 0; i < normalizedGears.length - 1; i += 1) {
      const driver = normalizedGears[i];
      const driven = normalizedGears[i + 1];

      if (driver.shaftId === driven.shaftId) {
        continue;
      }

      const stageRatioValue = driven.teeth / driver.teeth;
      const simplifiedStage = simplifyRatio(driven.teeth, driver.teeth);

      stageCount += 1;

      stageRatios.push({
        stage: stageCount,
        driverGearIndex: i + 1,
        drivenGearIndex: i + 2,
        driverTeeth: driver.teeth,
        drivenTeeth: driven.teeth,
        driverShaftId: driver.shaftId,
        drivenShaftId: driven.shaftId,
        ratio: formatRatio(stageRatioValue),
        ratioText: simplifiedStage.text,
        rawRatioText: `${driven.teeth}:${driver.teeth}`,
      });

      overallNumerator *= driven.teeth;
      overallDenominator *= driver.teeth;
    }

    if (stageRatios.length === 0) {
      errors.gearTrain =
        "At least one meshing stage is required. Adjacent gears on the same shaft do not create a ratio change.";
      return { errors, confidence: "Invalid" };
    }

    const simplifiedOverall = simplifyRatio(
      overallNumerator,
      overallDenominator
    );

    if (normalizedGears.length > 8) {
      warnings.push(
        "Large compound trains can be harder to verify physically. Double-check stage order and shaft assignments."
      );
    }

    return {
      gearCount: normalizedGears.length,
      stageCount,
      stageRatios,
      overallRatio: formatRatio(overallNumerator / overallDenominator),
      overallRatioText: simplifiedOverall.text,
      overallRatioRawText: `${overallNumerator}:${overallDenominator}`,
      overallRatioNumerator: simplifiedOverall.numerator,
      overallRatioDenominator: simplifiedOverall.denominator,
      warnings,
      confidence: warnings.length === 0 ? "High" : "Medium",
    };
  }, [gearTrain]);

  const activeResults = mode === "ratio" ? ratioResults : cuttingResults;
  const hasErrors =
    activeResults?.errors && Object.keys(activeResults.errors).length > 0;

  const handleGearTeethChange = (gearId, value) => {
    setGearTrain((prev) =>
      prev.map((gear) =>
        gear.id === gearId
          ? {
              ...gear,
              teeth: value === "" ? "" : parseFloat(value),
            }
          : gear
      )
    );
  };

  const handleGearShaftChange = (gearId, value) => {
    setGearTrain((prev) =>
      prev.map((gear) =>
        gear.id === gearId
          ? {
              ...gear,
              shaftId: value,
            }
          : gear
      )
    );
  };

  const addGear = () => {
    const lastGear = gearTrain[gearTrain.length - 1];
    const nextShaftId = createShaftId();

    setGearTrain((prev) => [
      ...prev,
      createGear(createGearId(), 20, lastGear?.shaftId || nextShaftId),
      createGear(createGearId(), 40, nextShaftId),
    ]);
  };

  const addGearOnSameShaft = (gearId) => {
    const sourceGear = gearTrain.find((gear) => gear.id === gearId);
    if (!sourceGear) return;

    const newGear = createGear(createGearId(), 20, sourceGear.shaftId);
    const insertIndex = gearTrain.findIndex((gear) => gear.id === gearId);

    setGearTrain((prev) => {
      const updated = [...prev];
      updated.splice(insertIndex + 1, 0, newGear);
      return updated;
    });
  };

  const removeGear = (gearId) => {
    setGearTrain((prev) => {
      if (prev.length <= 2) return prev;
      return prev.filter((gear) => gear.id !== gearId);
    });
  };

  const resetRatio = () => {
    setGearTrain([
      createGear("gear_1", 20, "shaft_1"),
      createGear("gear_2", 40, "shaft_2"),
    ]);
  };

  const handleCuttingNumberChange = (field, value) => {
    setCuttingParams((prev) => ({
      ...prev,
      [field]: value === "" ? "" : parseFloat(value),
    }));
  };

  const resetCutting = () => {
    setCuttingParams({
      teeth: 24,
      diametralPitch: 16,
    });
  };

  const copyToClipboard = () => {
    if (!activeResults || hasErrors) return;

    let text = "ManuCalc - Gear Ratio / Gear Cutting Calculator\n";

    if (mode === "ratio") {
      text += [
        "Mode: Gear Ratio",
        ...gearTrain.map(
          (gear, index) =>
            `Gear ${index + 1}: ${gear.teeth}T | Shaft ${gear.shaftId}`
        ),
        ...activeResults.stageRatios.map(
          (stage) =>
            `Stage ${stage.stage}: Gear ${stage.driverGearIndex} (${stage.driverTeeth}T, ${stage.driverShaftId}) -> Gear ${stage.drivenGearIndex} (${stage.drivenTeeth}T, ${stage.drivenShaftId}) = ${stage.ratio} (${stage.ratioText})`
        ),
        `Overall Ratio Decimal: ${activeResults.overallRatio}`,
        `Overall Ratio Simplified: ${activeResults.overallRatioText}`,
      ].join("\n");
    } else {
      text += [
        "Mode: Gear Cutting",
        `Teeth: ${cuttingParams.teeth}`,
        `Diametral Pitch: ${cuttingParams.diametralPitch}`,
        `Pitch Diameter: ${activeResults.pitchDiameter}"`,
        `Outside Diameter: ${activeResults.outsideDiameter}"`,
        `Root Diameter: ${activeResults.rootDiameter}"`,
        `Circular Pitch: ${activeResults.circularPitch}"`,
        `Addendum: ${activeResults.addendum}"`,
        `Dedendum: ${activeResults.dedendum}"`,
        `Whole Depth: ${activeResults.wholeDepth}"`,
      ].join("\n");
    }

    navigator.clipboard.writeText(text);
  };

  const handleSave = () => {
    if (!activeResults || hasErrors || !onSave) return;

    if (mode === "ratio") {
      onSave({
        summary: `${gearTrain.length} gears | Ratio ${activeResults.overallRatioText}`,
        input: {
          mode: "Gear Ratio",
          gears: gearTrain.map((gear, index) => ({
            position: index + 1,
            teeth: gear.teeth,
            shaftId: gear.shaftId,
          })),
        },
        results: {
          gearCount: activeResults.gearCount,
          stageCount: activeResults.stageCount,
          overallRatio: activeResults.overallRatio,
          overallRatioText: activeResults.overallRatioText,
          stageRatios: activeResults.stageRatios,
        },
        warnings: Array.isArray(activeResults.warnings)
          ? activeResults.warnings
          : [],
        confidence: activeResults.confidence || "N/A",
      });
      return;
    }

    onSave({
      summary: `${cuttingParams.teeth}T | ${cuttingParams.diametralPitch} DP | OD ${activeResults.outsideDiameter}"`,
      input: {
        mode: "Gear Cutting",
        teeth: cuttingParams.teeth,
        diametralPitch: cuttingParams.diametralPitch,
      },
      results: {
        pitchDiameter: activeResults.pitchDiameter,
        outsideDiameter: activeResults.outsideDiameter,
        rootDiameter: activeResults.rootDiameter,
        circularPitch: activeResults.circularPitch,
        addendum: activeResults.addendum,
        dedendum: activeResults.dedendum,
        wholeDepth: activeResults.wholeDepth,
      },
      warnings: Array.isArray(activeResults.warnings)
        ? activeResults.warnings
        : [],
      confidence: activeResults.confidence || "N/A",
    });
  };

  return (
    <div
      className="calculator-container"
      style={{
        background: "var(--bg-surface)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "24px",
      }}
    >
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          ⚙️ Gear Ratio / Gear Cutting
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Build a simple or compound gear train ratio, or calculate standard
          diametral pitch spur gear dimensions.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "24px",
        }}
      >
        <button
          type="button"
          onClick={() => setMode("ratio")}
          className={mode === "ratio" ? "btn-primary" : "btn-secondary"}
        >
          Gear Ratio
        </button>
        <button
          type="button"
          onClick={() => setMode("cutting")}
          className={mode === "cutting" ? "btn-primary" : "btn-secondary"}
        >
          Gear Cutting
        </button>
      </div>

      <div
        className="calculator-content"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "24px",
        }}
      >
        <div
          className="calculator-form"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "16px",
            padding: "20px",
          }}
        >
          {mode === "ratio" ? (
            <>
              <div
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  borderRadius: "14px",
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(59,130,246,0.06))",
                  border: "1px solid rgba(99,102,241,0.18)",
                }}
              >
                <div
                  style={{
                    color: "var(--text-primary)",
                    fontWeight: 700,
                    marginBottom: "8px",
                    fontSize: "16px",
                  }}
                >
                  Gear Train Builder
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: 1.6 }}>
                  Use matching shaft IDs for compound gears on the same shaft.
                  Adjacent gears with different shaft IDs create active meshing stages.
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: "14px",
                  marginBottom: "18px",
                }}
              >
                <ManufacturingDiagram toolId="gear-ratio" gears={gearTrain} />
                {gearTrain.map((gear, index) => {
                  const nextGear = gearTrain[index + 1];
                  const sameShaftAsNext =
                    nextGear && gear.shaftId === nextGear.shaftId;

                  return (
                    <div key={gear.id}>
                      <div
                        style={{
                          padding: "16px",
                          background: "var(--bg-surface)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "16px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "14px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "999px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, rgba(99,102,241,0.22), rgba(59,130,246,0.12))",
                                border: "1px solid rgba(99,102,241,0.25)",
                                color: "var(--text-primary)",
                                fontWeight: 800,
                                fontSize: "14px",
                              }}
                            >
                              {index + 1}
                            </div>

                            <div>
                              <div
                                style={{
                                  color: "var(--text-primary)",
                                  fontWeight: 700,
                                  fontSize: "15px",
                                }}
                              >
                                Gear {index + 1}
                              </div>
                              <div
                                style={{
                                  color: "var(--text-muted)",
                                  fontSize: "12px",
                                  marginTop: "2px",
                                }}
                              >
                                {index === 0
                                  ? "Input gear"
                                  : index === gearTrain.length - 1
                                  ? "Output gear"
                                  : "Intermediate gear"}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                padding: "6px 10px",
                                borderRadius: "999px",
                                background: "rgba(99,102,241,0.12)",
                                border: "1px solid rgba(99,102,241,0.20)",
                                color: "var(--text-primary)",
                                fontSize: "12px",
                                fontWeight: 700,
                              }}
                            >
                              {gear.shaftId}
                            </div>

                            {gearTrain.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeGear(gear.id)}
                                className="btn-secondary"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: "12px",
                          }}
                        >
                          <div>
                            <label
                              htmlFor={`gear-teeth-${gear.id}`}
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                color: "var(--text-primary)",
                                fontWeight: 600,
                              }}
                            >
                              Tooth Count
                            </label>
                            <input
                              id={`gear-teeth-${gear.id}`}
                              type="number"
                              min="1"
                              step="1"
                              value={gear.teeth}
                              onChange={(e) =>
                                handleGearTeethChange(gear.id, e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "11px 12px",
                                borderRadius: "10px",
                                border: "1px solid var(--border-color)",
                                background: "var(--bg-surface-alt)",
                                color: "var(--text-primary)",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor={`gear-shaft-${gear.id}`}
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                color: "var(--text-primary)",
                                fontWeight: 600,
                              }}
                            >
                              Shaft ID
                            </label>
                            <input
                              id={`gear-shaft-${gear.id}`}
                              type="text"
                              value={gear.shaftId}
                              onChange={(e) =>
                                handleGearShaftChange(gear.id, e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "11px 12px",
                                borderRadius: "10px",
                                border: "1px solid var(--border-color)",
                                background: "var(--bg-surface-alt)",
                                color: "var(--text-primary)",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                            marginTop: "14px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => addGearOnSameShaft(gear.id)}
                            className="btn-secondary"
                          >
                            + Add Gear On Same Shaft
                          </button>
                        </div>
                      </div>

                      {nextGear && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 4px",
                            marginLeft: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "2px",
                              height: "24px",
                              background: sameShaftAsNext
                                ? "rgba(16,185,129,0.55)"
                                : "rgba(59,130,246,0.45)",
                              borderRadius: "999px",
                            }}
                          />
                          <div
                            style={{
                              padding: "6px 10px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "var(--text-primary)",
                              background: sameShaftAsNext
                                ? "rgba(16,185,129,0.14)"
                                : "rgba(59,130,246,0.14)",
                              border: sameShaftAsNext
                                ? "1px solid rgba(16,185,129,0.24)"
                                : "1px solid rgba(59,130,246,0.22)",
                            }}
                          >
                            {sameShaftAsNext ? "Same Shaft" : "Meshing Stage"}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                className="form-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button onClick={addGear} className="btn-primary" type="button">
                  + Add Meshing Stage
                </button>
                <button
                  onClick={resetRatio}
                  className="btn-secondary"
                  type="button"
                >
                  Reset
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="teeth"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Tooth Count
                </label>
                <input
                  id="teeth"
                  type="number"
                  min="1"
                  step="1"
                  value={cuttingParams.teeth}
                  onChange={(e) =>
                    handleCuttingNumberChange("teeth", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="diametralPitch"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Diametral Pitch
                </label>
                <input
                  id="diametralPitch"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  value={cuttingParams.diametralPitch}
                  onChange={(e) =>
                    handleCuttingNumberChange("diametralPitch", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div
                className="form-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={resetCutting}
                  className="btn-secondary"
                  type="button"
                >
                  Reset
                </button>
              </div>
            </>
          )}
        </div>

        <div
          className="calculator-results"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
            minHeight: "100%",
          }}
        >
          {activeResults && !hasErrors ? (
            <>
              <div
                className="results-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                  Results
                </h3>
                <div
                  className="confidence-indicator"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Confidence:{" "}
                  <span style={{ color: "var(--accent)", fontWeight: 700 }}>
                    {activeResults.confidence}
                  </span>
                </div>
              </div>

              {mode === "ratio" ? (
                <>
                  <div
                    className="results-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "12px",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      className="result-item"
                      style={{
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "10px",
                        padding: "14px",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          color: "var(--text-muted)",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        Overall Ratio
                      </label>
                      <span
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          fontSize: "18px",
                        }}
                      >
                        {activeResults.overallRatio}
                      </span>
                    </div>

                    <div
                      className="result-item"
                      style={{
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "10px",
                        padding: "14px",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          color: "var(--text-muted)",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        Simplified Ratio
                      </label>
                      <span
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          fontSize: "18px",
                        }}
                      >
                        {activeResults.overallRatioText}
                      </span>
                    </div>

                    <div
                      className="result-item"
                      style={{
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "10px",
                        padding: "14px",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          color: "var(--text-muted)",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        Active Stages
                      </label>
                      <span
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          fontSize: "18px",
                        }}
                      >
                        {activeResults.stageCount}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginTop: 0,
                        marginBottom: "10px",
                      }}
                    >
                      Stage Ratios
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                      }}
                    >
                      {activeResults.stageRatios.map((stage) => (
                        <div
                          key={stage.stage}
                          style={{
                            padding: "12px",
                            borderRadius: "8px",
                            border: "1px solid var(--border-color)",
                            background: "var(--bg-surface-alt)",
                            color: "var(--text-secondary)",
                          }}
                        >
                          <div
                            style={{
                              color: "var(--text-primary)",
                              fontWeight: 700,
                              marginBottom: "4px",
                            }}
                          >
                            Stage {stage.stage}
                          </div>
                          <div>
                            Gear {stage.driverGearIndex} ({stage.driverTeeth}T,{" "}
                            {stage.driverShaftId}) → Gear {stage.drivenGearIndex}{" "}
                            ({stage.drivenTeeth}T, {stage.drivenShaftId})
                          </div>
                          <div>
                            Ratio:{" "}
                            <strong style={{ color: "var(--text-primary)" }}>
                              {stage.ratio}
                            </strong>
                          </div>
                          <div>
                            Simplified:{" "}
                            <strong style={{ color: "var(--text-primary)" }}>
                              {stage.ratioText}
                            </strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginTop: 0,
                        marginBottom: "10px",
                      }}
                    >
                      Selection Summary
                    </h4>
                    <div
                      style={{
                        color: "var(--text-secondary)",
                        lineHeight: 1.6,
                      }}
                    >
                      {gearTrain.map((gear, index) => (
                        <div key={gear.id}>
                          <strong style={{ color: "var(--text-primary)" }}>
                            Gear {index + 1}:
                          </strong>{" "}
                          {gear.teeth} teeth on {gear.shaftId}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="results-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "12px",
                      marginBottom: "20px",
                    }}
                  >
                    {[
                      ["Pitch Diameter", `${activeResults.pitchDiameter}"`],
                      ["Outside Diameter", `${activeResults.outsideDiameter}"`],
                      ["Root Diameter", `${activeResults.rootDiameter}"`],
                      ["Circular Pitch", `${activeResults.circularPitch}"`],
                      ["Addendum", `${activeResults.addendum}"`],
                      ["Dedendum", `${activeResults.dedendum}"`],
                      ["Whole Depth", `${activeResults.wholeDepth}"`],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="result-item"
                        style={{
                          background: "var(--bg-surface)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "10px",
                          padding: "14px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            color: "var(--text-muted)",
                            fontSize: "14px",
                            marginBottom: "6px",
                          }}
                        >
                          {label}
                        </label>
                        <span
                          style={{
                            color: "var(--text-primary)",
                            fontWeight: 700,
                            fontSize: "18px",
                          }}
                        >
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginTop: 0,
                        marginBottom: "10px",
                      }}
                    >
                      Selection Summary
                    </h4>
                    <div
                      style={{
                        color: "var(--text-secondary)",
                        lineHeight: 1.6,
                      }}
                    >
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>
                          Tooth Count:
                        </strong>{" "}
                        {cuttingParams.teeth}
                      </div>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>
                          Diametral Pitch:
                        </strong>{" "}
                        {cuttingParams.diametralPitch}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {Array.isArray(activeResults.warnings) &&
                activeResults.warnings.length > 0 && (
                  <div
                    className="warnings"
                    style={{
                      background: "rgba(245, 158, 11, 0.12)",
                      border: "1px solid rgba(245, 158, 11, 0.35)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginBottom: "10px",
                      }}
                    >
                      ⚠️ Warnings
                    </h4>
                    <ul
                      style={{
                        paddingLeft: "20px",
                        color: "var(--text-secondary)",
                        marginBottom: 0,
                      }}
                    >
                      {activeResults.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

              <div
                className="results-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  type="button"
                  disabled={savingCalculation}
                >
                  {savingCalculation ? "Saving..." : "💾 Save Calculation"}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary"
                  type="button"
                >
                  📄 Copy
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "180px",
                textAlign: "center",
              }}
            >
              Enter valid inputs to calculate gear ratio or gear dimensions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GearRatioCalculator;
